import React, { useState } from 'react';
import { Trophy, ChevronRight, ChevronDown, Settings, BookOpen, X, ShieldAlert, Sparkles, Edit2, Check, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { CardUI } from './CardUI';
import { Card, Suit } from '../types';
import { useSettings, CardBackTheme, TableTheme } from './SettingsContext';
import { playSound, triggerVibration } from '../lib/soundUtils';
import { cn } from '../lib/utils';
import { UserProfile, getLevelProgress } from '../lib/progression';
import { formatCoins } from '../hooks/useCoins';
import { createCampaignUrl } from '../lib/analytics';
import LeaderboardModal from './LeaderboardModal';
import AdSenseBanner from './AdSenseBanner';

interface MainMenuProps {
  profile: UserProfile | null;
  profileLoading: boolean;
  onUpdateName: (newName: string) => void;
  onUpdateCustomization: (avatar: string, title: string) => void;
  onQuickPlay: () => void;
  onPlayVsCPU: () => void;
  onPlayOnline: () => void;
  user: any;
  logout: () => Promise<void>;
  onOpenAuth: () => void;
  onManageCookies?: () => void;
  coinsBalance?: number;
  onOpenShop?: () => void;
  onOpenAchievements?: () => void;
  onOpenFriends?: () => void;
  onOpenDailyChallenges?: () => void;
  onOpenTournament?: () => void;
}

const MENU_CARDS: Card[] = [
  { id: 'menu-1', value: 1, suit: Suit.COINS }, // Asso di Denari
  { id: 'menu-2', value: 7, suit: Suit.COINS }, // Settebello (7 di Denari)
  { id: 'menu-3', value: 10, suit: Suit.CLUBS }, // Re di Bastoni
];

const AVATARS = [
  { emoji: '🪙', label: 'Moneta', level: 1 },
  { emoji: '🍷', label: 'Coppa', level: 2 },
  { emoji: '⚔️', label: 'Spada', level: 3 },
  { emoji: '🌿', label: 'Bastone', level: 4 },
  { emoji: '👑', label: 'Corona', level: 5 },
  { emoji: '🃏', label: 'Joker', level: 7 },
  { emoji: '🏆', label: 'Trofeo', level: 10 }
];

const TITLES = [
  { text: 'Novellino', level: 1 },
  { text: 'Sbarazzatore', level: 3 },
  { text: 'Stratega dei Denari', level: 5 },
  { text: 'Cavaliere di Spade', level: 8 },
  { text: 'Leggenda di Sbarazzo', level: 10 }
];

export default function MainMenu({ 
  profile, 
  profileLoading, 
  onUpdateName, 
  onUpdateCustomization,
  onQuickPlay,
  onPlayVsCPU, 
  onPlayOnline,
  user,
  logout,
  onOpenAuth,
  onManageCookies,
  coinsBalance,
  onOpenShop,
  onOpenAchievements,
  onOpenFriends,
  onOpenDailyChallenges,
  onOpenTournament
}: MainMenuProps) {
  const {
    sfxEnabled,
    vibrationEnabled,
    cardBackTheme,
    tableTheme,
    setSfxEnabled,
    setVibrationEnabled,
    setCardBackTheme,
    setTableTheme,
    playSfx,
    triggerHaptic,
  } = useSettings();

  const [showSettings, setShowSettings] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [profileTab, setProfileTab] = useState<'stats' | 'history'>('stats');
  const dailyHandUrl = createCampaignUrl('/mano-del-giorno/', 'daily_hand_menu', 'product', 'play_menu');

  const [adFreeGames, setAdFreeGames] = useState(() => {
    try {
      const raw = localStorage.getItem('sbarazzo_video_boost');
      if (raw) return JSON.parse(raw).adFreeGames || 0;
    } catch {}
    return 0;
  });

  const [cookieConsent, setCookieConsent] = useState(() => {
    return localStorage.getItem('sbarazzo_cookie_consent');
  });

  React.useEffect(() => {
    const handleBoost = () => {
      try {
        const raw = localStorage.getItem('sbarazzo_video_boost');
        if (raw) setAdFreeGames(JSON.parse(raw).adFreeGames || 0);
        else setAdFreeGames(0);
      } catch {}
    };
    handleBoost();
    window.addEventListener('video_boost_updated', handleBoost);
    return () => window.removeEventListener('video_boost_updated', handleBoost);
  }, []);

  React.useEffect(() => {
    const handleConsent = () => {
      setCookieConsent(localStorage.getItem('sbarazzo_cookie_consent'));
    };
    window.addEventListener('cookie_consent_updated', handleConsent);
    return () => window.removeEventListener('cookie_consent_updated', handleConsent);
  }, []);

  // Name Editing State
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');

  const startEditing = () => {
    setEditNameValue(profile?.displayName || '');
    setIsEditingName(true);
  };

  const saveName = () => {
    const trimmed = editNameValue.trim();
    if (trimmed && trimmed !== profile?.displayName) {
      onUpdateName(trimmed);
    }
    setIsEditingName(false);
  };

  return (
    <div className={cn("fixed inset-0 game-bg text-white font-sans select-none overflow-y-auto", `game-bg-${tableTheme}`)}>
      <div className="min-h-[100dvh] w-full flex flex-col items-center justify-start p-4 pb-8">
        {/* Secondary actions stay available without competing with the first play. */}
        <details className="w-full max-w-md z-10 shrink-0 group">
          <summary className="list-none cursor-pointer flex items-center justify-center gap-2 py-2 px-4 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400">
            <span>Altro</span>
            <ChevronDown className="w-4 h-4 text-white/60 transition-transform group-open:rotate-180" />
          </summary>
          <div className="w-full flex flex-col gap-2 pt-2"
            style={{ paddingTop: "0.5rem" }}
          >
        {/* Row 1: Regole, Classifica, Obiettivi */}
        <div className="flex gap-2">
        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            setShowRulesModal(true);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <BookOpen className="w-3.5 h-3.5 text-yellow-400" />
          <span>Regole</span>
        </button>

        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            setShowLeaderboard(true);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <Trophy className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
          <span>Classifica</span>
        </button>

        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onOpenAchievements?.();
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <Trophy className="w-3.5 h-3.5 text-yellow-400" />
          <span>Obiettivi</span>
        </button>
        </div>

        {/* Row 2: Opzioni, Negozio, Amici */}
        <div className="flex gap-2">
        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            setShowSettings(true);
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <Settings className="w-3.5 h-3.5 text-yellow-400" />
          <span>Opzioni</span>
        </button>

        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onOpenShop?.();
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <span className="text-yellow-400">🪙</span>
          <span>Negozio</span>
        </button>

        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onOpenFriends?.();
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <span className="text-yellow-400">👥</span>
          <span>Amici</span>
        </button>
        </div>

        {/* Row 3: Sfide, Torneo */}
        <div className="flex gap-2">
        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onOpenDailyChallenges?.();
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <span className="text-yellow-400">🎯</span>
          <span>Sfide</span>
        </button>

        <button
          onClick={() => {
            playSfx('click');
            triggerHaptic('light');
            onOpenTournament?.();
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 glass-panel hover:bg-white/10 rounded-full text-xs font-bold transition-all"
        >
          <Trophy className="w-3.5 h-3.5 text-yellow-400" />
          <span>Torneo</span>
        </button>
        </div>
        </div>
        </details>

      {coinsBalance !== undefined && (
        <div className="w-full max-w-md flex justify-end z-10 shrink-0" style={{ paddingTop: "0.25rem" }}>
          <div className="flex items-center gap-1 px-3 py-2 glass-panel rounded-full shrink-0">
            <span className="text-yellow-400 font-bold text-sm">🪙</span>
            <span className="text-white font-bold text-sm tabular-nums">{formatCoins(coinsBalance)}</span>
          </div>
        </div>
      )}

      <div className="w-full max-w-md my-auto py-6 text-center space-y-6 sm:space-y-8 z-10 flex flex-col">
        {/* Title Section */}
        <div className="space-y-1 sm:space-y-2 order-1">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl sm:text-6xl md:text-7xl font-black tracking-[0.12em] uppercase text-yellow-400" 
            style={{ fontFamily: '"Playfair Display", serif', textShadow: '0 0 40px rgba(251,191,36,0.35)' }}
          >
            SBARAZZINO
          </motion.h1>
          <p className="text-white/50 text-[10px] sm:text-xs uppercase tracking-[0.25em]">Scopa Speciale · Carte Napoletane</p>
          
          <p className="text-white/70 text-xs sm:text-sm leading-relaxed max-w-sm mx-auto mt-3 px-4">
            La Scopa dove una presa sbagliata può costarti 10 punti.
            <a href="/how-to-play.html"
              className="text-yellow-400/80 hover:text-yellow-400 underline underline-offset-2 ml-1">Scopri le regole →</a>
          </p>
        </div>

        {/* 3D Interactive Card Fan */}
        <div className="flex justify-center items-center h-28 sm:h-36 relative order-3" style={{ perspective: '1000px' }}>
          {MENU_CARDS.map((card, i) => {
            const rotation = (i - 1) * 15; // -15deg, 0deg, 15deg
            const xOffset = (i - 1) * 36; // -36px, 0px, 36px
            const yOffset = Math.abs(i - 1) * 6; // 6px, 0px, 6px
            return (
              <motion.div
                key={card.id}
                initial={{ opacity: 0, rotate: 0, x: 0, y: 50 }}
                animate={{ opacity: 1, rotate: rotation, x: xOffset, y: yOffset }}
                whileHover={{ 
                  scale: 1.18, 
                  y: yOffset - 18,
                  zIndex: 20,
                  filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.6))',
                  transition: { type: 'spring', stiffness: 300, damping: 15 }
                }}
                transition={{ type: 'spring', stiffness: 120, damping: 14, delay: i * 0.15 }}
                className="absolute origin-bottom cursor-pointer"
                style={{ zIndex: i }}
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                }}
              >
                <CardUI card={card} disabled scale={1.1} />
              </motion.div>
            );
          })}
        </div>

        {/* Profile Progression Panel */}
        {profileLoading ? (
          <div className="w-full glass-panel rounded-3xl p-5 flex flex-col items-center justify-center min-h-[140px] space-y-2 border border-white/10">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-400" />
            <p className="text-white/40 text-xs">Caricamento profilo...</p>
          </div>
        ) : profile ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.35 }}
            className="w-full glass-panel rounded-3xl p-5 space-y-4 border border-white/10 text-left relative overflow-hidden order-4"
          >
            {/* Background design glow */}
            <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-yellow-400/5 rounded-full blur-xl pointer-events-none" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Avatar with Level Badge Overlay */}
                <div 
                  onClick={() => {
                    playSfx('click');
                    triggerHaptic('light');
                    setShowCustomizer(true);
                  }}
                  className="w-12 h-12 rounded-full border border-white/15 hover:border-yellow-400/60 flex items-center justify-center bg-black/45 relative shadow-md cursor-pointer group shrink-0 transition-all hover:scale-105"
                  title="Personalizza avatar e titolo"
                >
                  <span className="text-2xl group-hover:scale-110 transition-transform">{profile.avatar || '🪙'}</span>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-yellow-500 text-black flex items-center justify-center border border-black font-black text-[9px]">
                    {profile.level}
                  </div>
                </div>

                {/* Name / Display Section */}
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    {isEditingName ? (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={editNameValue}
                          onChange={(e) => setEditNameValue(e.target.value.slice(0, 20))}
                          className="bg-black/60 border border-yellow-500/50 rounded-lg px-2 py-0.5 text-sm text-white focus:outline-none focus:border-yellow-500 max-w-[140px]"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveName();
                            if (e.key === 'Escape') setIsEditingName(false);
                          }}
                        />
                        <button
                          onClick={saveName}
                          className="p-1 hover:bg-white/10 rounded text-green-400 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="font-bold text-white text-base max-w-[160px] truncate">
                          {profile.displayName}
                        </span>
                        <button
                          onClick={startEditing}
                          className="p-1 hover:bg-white/10 rounded text-white/40 hover:text-white/85 transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                  {profile.title && (
                    <div className="text-[9px] text-yellow-400/85 font-black uppercase tracking-wider -mt-1 ml-0.5">
                      {profile.title}
                    </div>
                  )}
                  <div className="flex items-center flex-wrap gap-x-2">
                    <p className="text-[10px] text-white/40 uppercase tracking-widest font-semibold">Profilo Giocatore</p>
                    {user?.isAnonymous ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          playSfx('click');
                          triggerHaptic('light');
                          onOpenAuth();
                        }}
                        className="text-[9px] bg-yellow-500 hover:bg-yellow-400 text-black px-2 py-0.5 rounded-full font-bold uppercase tracking-wider transition-all scale-[0.9] origin-left"
                      >
                        Salva Progressi
                      </button>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] text-white/50 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded-full truncate max-w-[120px]">
                          {user?.email || 'Google'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            playSfx('click');
                            triggerHaptic('light');
                            try {
                              await logout();
                            } catch (err) {
                              console.error('Error logging out:', err);
                            }
                          }}
                          className="text-[9px] text-red-400 hover:text-red-300 font-bold uppercase transition-colors"
                        >
                          Esci
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick XP display */}
              <div className="text-right shrink-0">
                <span className="text-xs font-bold text-yellow-400/90">{profile.xp} <span className="text-[9px] text-white/40 font-normal">XP Totali</span></span>
              </div>
            </div>

            {/* Profile Tabs */}
            <div className="flex border-b border-white/5 pb-1.5 gap-4 text-[10px] font-bold uppercase tracking-wider text-white/40">
              <button 
                onClick={() => setProfileTab('stats')}
                className={cn("hover:text-white transition-colors pb-0.5", profileTab === 'stats' && "text-yellow-400 border-b-2 border-yellow-400 font-extrabold")}
              >
                Statistiche
              </button>
              <button 
                onClick={() => setProfileTab('history')}
                className={cn("hover:text-white transition-colors pb-0.5", profileTab === 'history' && "text-yellow-400 border-b-2 border-yellow-400 font-extrabold")}
              >
                Cronologia
              </button>
            </div>

            {profileTab === 'stats' ? (
              <>
                {/* Level Progress Bar */}
                {(() => {
                  const progress = getLevelProgress(profile.xp);
                  return (
                    <div className="space-y-1.5">
                      <div className="w-full h-2.5 bg-black/40 border border-white/5 rounded-full overflow-hidden p-[1px]">
                        <motion.div
                          className="h-full bg-gradient-to-r from-yellow-500 to-amber-400 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${progress.progressPercent}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                      </div>
                      <div className="flex justify-between text-[9px] text-white/40 font-bold uppercase tracking-wider">
                        <span>Lvl {progress.level} ({progress.xpInCurrentLevel} XP)</span>
                        <span>{Math.round(progress.progressPercent)}%</span>
                        <span>Lvl {progress.level + 1} ({progress.nextLevelXpStart} XP)</span>
                      </div>
                    </div>
                  );
                })()}

                {/* Warning banner for anonymous guests */}
                {user?.isAnonymous && (
                  <div className="mt-3 bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-3 flex items-center justify-between gap-3 shadow-[0_0_15px_rgba(234,179,8,0.05)]">
                    <div className="flex-1">
                      <p className="text-yellow-400 font-extrabold text-[10px] uppercase tracking-wider mb-0.5">🔒 Stai giocando come ospite</p>
                      <p className="text-white/75 text-[10px] sm:text-xs leading-tight">
                        Registrati gratis per salvare livello, vittorie, classifica e personalizzazioni in modo permanente.
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playSfx('click');
                        triggerHaptic('medium');
                        onOpenAuth();
                      }}
                      className="bg-yellow-500 hover:bg-yellow-400 text-black font-black uppercase text-[10px] px-3 py-1.5 rounded-xl tracking-wider transition-all whitespace-nowrap active:scale-95 cursor-pointer shrink-0"
                    >
                      Registrati
                    </button>
                  </div>
                )}

                {/* Micro Dividers */}
                <div className="h-px bg-white/5" />

                {/* Profile Statistics Grid */}
                <div className="grid grid-cols-4 gap-2 text-center pt-0.5">
                  <div>
                    <div className="text-lg font-black text-white/95" style={{ fontFamily: '"Playfair Display", serif' }}>{profile.stats.played}</div>
                    <div className="text-[8px] uppercase tracking-widest text-white/40 mt-0.5">Partite</div>
                  </div>
                  <div>
                    <div className="text-lg font-black text-green-400" style={{ fontFamily: '"Playfair Display", serif' }}>{profile.stats.won}</div>
                    <div className="text-[8px] uppercase tracking-widest text-white/40 mt-0.5">Vinte</div>
                  </div>
                  <div>
                    <div className="text-lg font-black text-red-400" style={{ fontFamily: '"Playfair Display", serif' }}>{profile.stats.lost}</div>
                    <div className="text-[8px] uppercase tracking-widest text-white/40 mt-0.5">Perse</div>
                  </div>
                  <div>
                    <div className="text-lg font-black text-cyan-400" style={{ fontFamily: '"Playfair Display", serif' }}>
                      {profile.stats.played > 0 ? Math.round((profile.stats.won / profile.stats.played) * 100) : 0}%
                    </div>
                    <div className="text-[8px] uppercase tracking-widest text-white/40 mt-0.5">Win Rate</div>
                  </div>
                </div>
              </>
            ) : (
              <div className="max-h-[140px] overflow-y-auto space-y-1.5 scrollbar-thin pr-1">
                {(!profile.history || profile.history.length === 0) ? (
                  <p className="text-xs text-white/30 text-center py-4">Nessuna partita giocata di recente.</p>
                ) : (
                  profile.history.map((record) => {
                    const dateStr = new Date(record.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
                    return (
                      <div 
                        key={record.id}
                        className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 border border-white/5 text-[11px]"
                      >
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "px-1.5 py-0.5 rounded font-black text-[9px] min-w-[16px] text-center",
                            record.won ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"
                          )}>
                            {record.won ? 'W' : 'L'}
                          </span>
                          <div>
                            <span className="font-semibold text-white/90">
                              {record.mode === 'cpu' ? 'Giocatore vs CPU' : `vs ${record.opponentName}`}
                            </span>
                            <span className="text-[9px] text-white/30 ml-2 font-medium">({dateStr})</span>
                          </div>
                        </div>
                        <div className="font-black text-white/80">
                          {record.myScore} - {record.opponentScore}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </motion.div>
        ) : null}

        {/* Buttons section */}
        <div className="flex flex-col gap-2.5 sm:gap-3 order-2">
          {/* TODO: riattivare pulsante Rimuovi Pubblicità in fase successiva */}
          {/* {cookieConsent === 'all' && (
            adFreeGames > 0 ? (
              <div className="w-full py-3 px-5 sm:px-6 bg-green-500/10 border border-green-500/20 rounded-xl sm:rounded-2xl text-left">
                <div className="text-sm font-bold text-green-400">🎁 Bonus Attivo</div>
                <div className="text-xs text-green-400/70 mt-0.5">Hai {adFreeGames} {adFreeGames === 1 ? 'partita' : 'partite'} senza pubblicità!</div>
              </div>
            ) : (
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  window.dispatchEvent(new CustomEvent('show_video_ad_modal', { detail: { mode: 'startup' } }));
                }}
                className="w-full py-3.5 px-5 sm:px-6 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/30 rounded-xl sm:rounded-2xl text-left transition-all active:scale-[0.98] group"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-base sm:text-lg font-black text-yellow-400">Rimuovi Pubblicità</div>
                    <div className="text-[10px] sm:text-xs text-yellow-400/70 mt-0.5">Guarda un video per giocare senza ads</div>
                  </div>
                  <Sparkles className="w-5 h-5 text-yellow-400 group-hover:scale-110 transition-transform" />
                </div>
              </button>
            )
          )} */}

          <section>
            <h2 className="sr-only">Gioca contro la CPU</h2>
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                onQuickPlay();
              }}
              className="w-full py-4 sm:py-5 px-5 sm:px-6 glass-panel-amber hover:brightness-110 rounded-xl sm:rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] group shadow-[0_8px_30px_rgba(234,179,8,0.14)]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl sm:text-2xl font-black text-yellow-300 transition-colors">Gioca subito</div>
                  <div className="text-[10px] sm:text-xs text-white/60 mt-0.5">Partita rapida · 2 giocatori · difficoltà media</div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-yellow-500 transition-colors" />
              </div>
            </button>
            <div className="flex items-center justify-center gap-3 text-[10px] sm:text-xs">
              <button
                type="button"
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  onPlayVsCPU();
                }}
                className="text-white/45 hover:text-yellow-300 underline underline-offset-4 transition-colors"
              >
                Personalizza partita
              </button>
            </div>
          </section>

          <a
            href={dailyHandUrl}
            className="w-full py-3.5 sm:py-4 px-5 sm:px-6 glass-panel hover:bg-yellow-400/10 border border-yellow-400/20 rounded-xl sm:rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] group"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-yellow-400/15 flex items-center justify-center shrink-0">
                  <Sparkles className="w-4 h-4 text-yellow-300" />
                </div>
                <div className="min-w-0">
                  <div className="text-base sm:text-lg font-black text-yellow-200 group-hover:text-yellow-100 transition-colors">Mano del giorno</div>
                  <div className="text-[10px] sm:text-xs text-white/50 mt-0.5">Una situazione nuova · risposta, spiegazione e condivisione</div>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-yellow-300 transition-colors shrink-0" />
            </div>
          </a>

          <section>
            <h2 className="sr-only">Multiplayer Online</h2>
            <button
              onClick={() => {
                playSfx('click');
                triggerHaptic('light');
                onPlayOnline();
              }}
              className="w-full py-3.5 sm:py-4 px-5 sm:px-6 glass-panel hover:glass-panel-rose rounded-xl sm:rounded-2xl text-left transition-all hover:scale-[1.02] active:scale-[0.98] group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-lg sm:text-xl font-black text-white group-hover:text-rose-400 transition-colors">Multiplayer Online</div>
                  <div className="text-[10px] sm:text-xs text-white/40 mt-0.5">Sfida un amico in tempo reale</div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-rose-500 transition-colors" />
              </div>
            </button>
          </section>
        </div>

        <div className="text-center pt-2 pb-1 space-y-1.5 order-5">
          <p className="text-[9px] text-white/20 uppercase tracking-widest">Traguardo della partita: 16 punti</p>
          <div className="flex justify-center gap-3 text-[10px] text-white/30">
            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Privacy Policy</a>
            <span>•</span>
            <a href="/cookie-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Cookie Policy</a>
            <span>•</span>
            <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Termini & Condizioni</a>
          </div>
        </div>

        <div className="w-full pt-2 shrink-0">
          <AdSenseBanner slot="4829301948" format="horizontal" />
        </div>
      </div>
      </div>

      {/* Settings Modal overlay */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 overflow-y-auto"
          >
            <div className="min-h-full flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="w-full max-w-md glass-panel p-5 sm:p-6 rounded-3xl space-y-5 sm:space-y-6 relative border border-white/10 my-8"
              >
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  setShowSettings(false);
                }}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>

              <h2 className="text-xl sm:text-2xl font-black uppercase text-yellow-400 tracking-wider">Impostazioni</h2>

              <div className="space-y-4">
                {/* Audio Option */}
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <div>
                    <div className="font-bold text-white text-sm sm:text-base">Effetti Sonori</div>
                    <div className="text-[10px] sm:text-xs text-white/40">Suoni durante le giocate</div>
                  </div>
                  <button
                    onClick={() => {
                      setSfxEnabled(!sfxEnabled);
                      playSfx('click');
                      triggerHaptic('light');
                    }}
                    className={cn(
                      "w-12 h-6 rounded-full transition-colors relative flex items-center px-1",
                      sfxEnabled ? "bg-yellow-500" : "bg-white/10"
                    )}
                  >
                    <motion.div
                      layout
                      className="w-4 h-4 rounded-full bg-black shadow"
                      animate={{ x: sfxEnabled ? 24 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </button>
                </div>

                {/* Vibration Option */}
                <div className="flex items-center justify-between py-2 border-b border-white/5">
                  <div>
                    <div className="font-bold text-white text-sm sm:text-base">Vibrazione (Haptic)</div>
                    <div className="text-[10px] sm:text-xs text-white/40">Feedback fisico su mosse speciali</div>
                  </div>
                  <button
                    onClick={() => {
                      setVibrationEnabled(!vibrationEnabled);
                      playSfx('click');
                      triggerVibration('light');
                    }}
                    className={cn(
                      "w-12 h-6 rounded-full transition-colors relative flex items-center px-1",
                      vibrationEnabled ? "bg-yellow-500" : "bg-white/10"
                    )}
                  >
                    <motion.div
                      layout
                      className="w-4 h-4 rounded-full bg-black shadow"
                      animate={{ x: vibrationEnabled ? 24 : 0 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  </button>
                </div>

                {/* Table Theme Option */}
                <div className="space-y-2 py-2 border-b border-white/5">
                  <div>
                    <div className="font-bold text-white text-sm sm:text-base">Colore del Tavolo</div>
                    <div className="text-[10px] sm:text-xs text-white/40">Scegli la tonalità del panno da gioco</div>
                  </div>
                  <div className="flex gap-4 pt-1">
                    {(['green', 'blue', 'red'] as TableTheme[]).map((theme) => (
                      <button
                        key={theme}
                        onClick={() => {
                          setTableTheme(theme);
                          playSfx('click');
                          triggerHaptic('light');
                        }}
                        className={cn(
                          "w-10 h-10 rounded-full border-2 transition-all flex items-center justify-center relative",
                          theme === 'green' ? "bg-[#1e4d2b]" : theme === 'blue' ? "bg-[#1e3a8a]" : "bg-[#7f1d1d]",
                          tableTheme === theme ? "border-yellow-400 scale-110 shadow-lg" : "border-transparent opacity-60 hover:opacity-100"
                        )}
                      >
                        {tableTheme === theme && <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Card Back Option */}
                <div className="space-y-2 py-2">
                  <div>
                    <div className="font-bold text-white text-sm sm:text-base">Retro delle Carte</div>
                    <div className="text-[10px] sm:text-xs text-white/40">Seleziona il design posteriore</div>
                  </div>
                  <div className="flex gap-3 sm:gap-4 pt-1">
                    {(['classic', 'gold', 'modern'] as CardBackTheme[]).map((theme) => (
                      <button
                        key={theme}
                        onClick={() => {
                          setCardBackTheme(theme);
                          playSfx('click');
                          triggerHaptic('light');
                        }}
                        className={cn(
                          "flex flex-col items-center gap-1.5 p-2 rounded-xl border-2 transition-all w-24 bg-white/5",
                          cardBackTheme === theme ? "border-yellow-400 scale-105 bg-white/10 shadow-md" : "border-transparent opacity-60 hover:opacity-100"
                        )}
                      >
                        <div className="pointer-events-none">
                          <CardUI card={{ id: 'preview', value: 1, suit: Suit.COINS }} isHidden scale={0.7} />
                        </div>
                        <span className="text-[10px] capitalize text-white/80 font-semibold">{theme}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Privacy & Cookie Option */}
                <div className="space-y-2 py-3 border-t border-white/5 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-white text-sm sm:text-base">Privacy & Cookie</div>
                      <div className="text-[10px] sm:text-xs text-white/40">Modifica le tue preferenze sui cookie e annunci</div>
                    </div>
                    <button
                      onClick={() => {
                        playSfx('click');
                        triggerHaptic('light');
                        setShowSettings(false);
                        if (onManageCookies) onManageCookies();
                      }}
                      className="px-3.5 py-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white font-extrabold text-[10px] uppercase tracking-wider transition-all active:scale-95 cursor-pointer"
                    >
                      Gestisci
                    </button>
                  </div>
                  <div className="flex gap-2 text-[10px] text-white/30 pt-1 border-t border-white/5">
                    <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Privacy Policy</a>
                    <span>•</span>
                    <a href="/cookie-policy.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Cookie Policy</a>
                    <span>•</span>
                    <a href="/terms.html" target="_blank" rel="noopener noreferrer" className="hover:text-white/60 hover:underline">Termini & Condizioni</a>
                  </div>
                </div>
              </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rules Modal overlay */}
      <AnimatePresence>
        {showRulesModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 overflow-y-auto"
          >
            <div className="min-h-full flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="w-full max-w-lg glass-panel p-5 sm:p-6 rounded-3xl relative border border-white/10 flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] my-4 sm:my-6"
              >
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  setShowRulesModal(false);
                }}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors z-10"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>

              <h2 className="text-xl sm:text-2xl font-black uppercase text-yellow-400 tracking-wider mb-4 shrink-0">Regolamento Sbarazzo</h2>

              <div className="overflow-y-auto pr-1 space-y-5 text-left text-sm text-white/80 scrollbar-thin">
                <section className="space-y-1">
                  <h3 className="font-bold text-white text-base">L'Obiettivo</h3>
                  <p className="text-white/70">
                    L'obiettivo è raggiungere 16 punti prima degli altri giocatori. Il gioco si svolge a smazzate successive finché qualcuno non supera il traguardo.
                  </p>
                </section>

                <section className="space-y-1">
                  <h3 className="font-bold text-white text-base">Prese e Cattura</h3>
                  <ul className="list-disc pl-5 text-white/70 space-y-1">
                    <li><strong>Presa semplice</strong>: Una carta prende una di pari valore sul tavolo.</li>
                    <li><strong>Presa somma</strong>: Una carta prende più carte la cui somma è pari al suo valore (es: un 7 prende 4 e 3).</li>
                    <li><strong>Sbarazzo</strong>: Se all'inizio del round la somma delle tue 3 carte è ≤ 8, ottieni subito <strong>+3 punti</strong>. Il bonus si ripete ogni volta che dichiari una mano bassa in un nuovo round. L'Asso non ha nulla a che fare con lo Sbarazzo — è una carta normale.</li>
                  </ul>
                </section>

                <section className="space-y-1">
                  <h3 className="font-bold text-white text-base flex items-center gap-1.5">
                    <span>Punteggio del Round</span>
                    <Sparkles className="w-4 h-4 text-yellow-400" />
                  </h3>
                  <p className="text-white/70">A fine smazzata si contano i seguenti punti:</p>
                  <ul className="list-disc pl-5 text-white/70 space-y-1">
                    <li><strong>Scopa</strong>: +1 punto ogni volta che lasci il tavolo vuoto al termine del tuo turno.</li>
                    <li><strong>Sbarazzo (Mano Bassa)</strong>: +3 punti ogni volta che dichiari una mano con somma ≤ 8 (può ripetersi ogni round).</li>
                    <li><strong>Carte</strong>: +1 punto a chi raccoglie più carte (in caso di parità, 0 punti).</li>
                    <li><strong>Denari</strong>: +1 punto a chi prende più carte di Denari (in caso di parità, 0 punti).</li>
                    <li><strong>Settebello</strong>: +1 punto a chi cattura il 7 di Denari.</li>
                    <li><strong>Dieci Denari</strong>: +1 punto a chi cattura il 10 di Denari.</li>
                    <li><strong>Due Denari (Malus!)</strong>: <strong className="text-red-400 flex items-center gap-1 inline-flex bg-red-950/30 px-1.5 py-0.5 rounded border border-red-500/20"><ShieldAlert className="w-3.5 h-3.5 text-red-500 inline" /> -5 punti</strong> a chi raccoglie il 2 di Denari. Attenzione a evitarlo!</li>
                    <li><strong>Zero Denari (Malus!)</strong>: <strong className="text-red-400 flex items-center gap-1 inline-flex bg-red-950/30 px-1.5 py-0.5 rounded border border-red-500/20"><ShieldAlert className="w-3.5 h-3.5 text-red-500 inline" /> -10 punti</strong> se hai catturato almeno una carta ma nessuna è di Denari. Attenzione!</li>
                    <li><strong>Primiera</strong>: +1 punto a chi ottiene la primiera migliore (in caso di parità, 0 punti).</li>
                  </ul>
                </section>

                <section className="space-y-1">
                  <h3 className="font-bold text-white text-base">Dichiarazioni a Inizio Round</h3>
                  <p className="text-white/70">Prima di giocare la prima carta del round, puoi dichiarare:</p>
                  <ul className="list-disc pl-5 text-white/70 space-y-1">
                    <li><strong>Mano Bassa</strong>: Se la somma delle tue 3 carte iniziali è ≤ 8, ottieni <strong>+3 punti</strong>. La dichiarazione si ripete ogni round in cui hai una mano idonea.</li>
                    <li><strong>Tre Assi</strong>: Se hai tutti e 3 gli assi in mano, ottieni <strong>+6 punti</strong>. Si può dichiarare ogni volta che si presenta la condizione.</li>
                  </ul>
                </section>
              </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Leaderboard Modal overlay */}
      <AnimatePresence>
        {showLeaderboard && (
          <LeaderboardModal 
            onClose={() => setShowLeaderboard(false)} 
            myUid={profile?.uid} 
            myProfile={profile}
            isAnonymous={user?.isAnonymous ?? true}
            onOpenAuth={() => {
              setShowLeaderboard(false);
              onOpenAuth();
            }}
          />
        )}
      </AnimatePresence>

      {/* Customizer Modal */}
      <AnimatePresence>
        {showCustomizer && profile && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <motion.div
                initial={{ scale: 0.95, y: 15, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                exit={{ scale: 0.95, y: 15, opacity: 0 }}
                className="w-full max-w-sm glass-panel rounded-3xl relative border border-white/10 flex flex-col p-6 max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3rem)] overflow-hidden my-4 sm:my-6"
              >
              {/* Close Button */}
              <button
                onClick={() => {
                  playSfx('click');
                  triggerHaptic('light');
                  setShowCustomizer(false);
                }}
                className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>

              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-black uppercase text-yellow-400 tracking-wider mb-5 pr-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Personalizza Profilo
              </h2>

              <div className="flex-1 overflow-y-auto space-y-6 pr-1 scrollbar-thin">
                {/* Avatar Selection */}
                <div className="space-y-2">
                  <h3 className="text-xs uppercase font-bold tracking-widest text-white/40 ml-1">Scegli il tuo Avatar</h3>
                  <div className="grid grid-cols-4 gap-2">
                    {AVATARS.map((av) => {
                      const isUnlocked = profile.level >= av.level;
                      const isSelected = profile.avatar === av.emoji;
                      return (
                        <button
                          key={av.emoji}
                          disabled={!isUnlocked}
                          onClick={() => {
                            playSfx('click');
                            triggerHaptic('light');
                            onUpdateCustomization(av.emoji, profile.title || 'Novellino');
                          }}
                          className={cn(
                            "h-14 rounded-2xl border flex flex-col items-center justify-center relative transition-all",
                            !isUnlocked 
                              ? "bg-black/20 border-white/5 opacity-40 cursor-not-allowed"
                              : isSelected
                                ? "glass-panel-amber border-yellow-500 text-white scale-[1.03] shadow-[0_4px_10px_rgba(234,179,8,0.15)]"
                                : "bg-white/5 border-white/5 hover:border-white/15 text-white/80"
                          )}
                          title={!isUnlocked ? `Sblocca al Livello ${av.level}` : av.label}
                        >
                          <span className="text-2xl">{av.emoji}</span>
                          {!isUnlocked && (
                            <span className="text-[7px] font-black uppercase text-yellow-500/80 mt-0.5">Lvl {av.level}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Title Selection */}
                <div className="space-y-2">
                  <h3 className="text-xs uppercase font-bold tracking-widest text-white/40 ml-1">Scegli il tuo Titolo</h3>
                  <div className="space-y-2">
                    {TITLES.map((t) => {
                      const isUnlocked = profile.level >= t.level;
                      const isSelected = profile.title === t.text;
                      return (
                        <button
                          key={t.text}
                          disabled={!isUnlocked}
                          onClick={() => {
                            playSfx('click');
                            triggerHaptic('light');
                            onUpdateCustomization(profile.avatar || '🪙', t.text);
                          }}
                          className={cn(
                            "w-full p-3 rounded-2xl border flex items-center justify-between transition-all text-left",
                            !isUnlocked 
                              ? "bg-black/20 border-white/5 opacity-40 cursor-not-allowed"
                              : isSelected
                                ? "glass-panel-amber border-yellow-500 font-bold scale-[1.01]"
                                : "bg-white/5 border-white/5 hover:border-white/15 text-white/80"
                          )}
                        >
                          <span className="text-xs font-semibold uppercase tracking-wider">{t.text}</span>
                          {!isUnlocked ? (
                            <span className="text-[8px] font-black uppercase text-yellow-500 bg-yellow-500/10 px-2 py-0.5 rounded-full">Sblocca a Lvl {t.level}</span>
                          ) : (
                            isSelected && <span className="text-[8px] bg-yellow-500 text-black px-2 py-0.5 rounded-full font-black uppercase">Attivo</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
